import { useEffect, useMemo, useState } from 'react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import {
  useCreateProviderFee,
  useUpdateProviderFee,
} from '@/billing/services/use-provider-fees';
import { ServiceTypeahead } from './service-typeahead';
import { DoctorShareDialog, type ShareInput } from './doctor-share-dialog';
import {
  blankDraft,
  buildPayload,
  draftFromFee,
  draftFromService,
  formatSharePreview,
  type ProviderFeeDraft,
} from './provider-fee-draft';
import type { BillingServiceModel, ProviderServiceFee } from '@/shared/types/billing';
import type { OrgLocation } from '@/auth/types';

/**
 * Inline create / edit row rendered as a real table `<tr>` with `<td>` cells
 * matching the table's column structure, so every input lines up under its
 * column header. PM parity: `provider-fee-edit-row.tsx`.
 *
 * Recomposed on Signal DS: PM lucide glyphs → DS `TextField` + shared
 * `ServiceTypeahead` + composed glyph buttons (DS registry lacks
 * reset/check/x — see report DS-GAP). No hardcoded hex.
 *
 * Column layout (mirrors `ProviderFeesTable` header):
 *   [checkbox] [Service] [WTAX]
 *   [LOC AED] [LOC SHARE] × N   (the LAST location's SHARE cell carries the
 *                                global Doctor Share button)
 *   [✓ ✗]
 */

interface ProviderFeeEditRowProps {
  providerId: string;
  locations: OrgLocation[];
  /** Pass the row's existing data to enter EDIT mode; omit for CREATE. */
  editing?: ProviderServiceFee | null;
  /** Fires after a successful update (EDIT) or after the user clicks ✗. */
  onDone: () => void;
  /** Render the field labels above the inputs. Default: true. */
  showLabels?: boolean;
}

export function ProviderFeeEditRow({
  providerId,
  locations,
  editing,
  onDone,
  showLabels = true,
}: ProviderFeeEditRowProps) {
  // LOOP-SAFETY: destructure the stable `mutateAsync` fns.
  const create = useCreateProviderFee();
  const update = useUpdateProviderFee();
  const { mutateAsync: createFee } = create;
  const { mutateAsync: updateFee } = update;
  const isEdit = Boolean(editing?.id);
  const submitting = create.isPending || update.isPending;

  const [draft, setDraft] = useState<ProviderFeeDraft>(() =>
    editing ? draftFromFee(editing, locations) : blankDraft(),
  );
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setDraft(editing ? draftFromFee(editing, locations) : blankDraft());
  }, [editing, locations]);

  const hasAnyFee = useMemo(
    () => Object.values(draft.fees).some((f) => f.fee.trim() && parseFloat(f.fee) > 0),
    [draft.fees],
  );
  const canSave = Boolean(draft.serviceId) && hasAnyFee;
  const shareCount = Object.keys(draft.shares).length;

  const reset = () => setDraft(blankDraft());

  const onSubmit = async () => {
    if (!canSave) return;
    const payload = buildPayload(draft, locations, providerId, editing ?? null);
    try {
      if (isEdit) {
        await updateFee(payload);
        onDone();
      } else {
        await createFee(payload);
        reset();
      }
    } catch {
      /* Global toast surfaces the failure. */
    }
  };

  const onCancel = () => {
    if (isEdit) onDone();
    else reset();
  };

  const onPickService = (svc: BillingServiceModel) => {
    setDraft((prev) => draftFromService(prev, svc, locations));
  };

  const clearService = () => {
    setDraft((p) => ({
      ...p,
      serviceId: undefined,
      procedureCode: undefined,
      serviceQuery: '',
      fees: {},
    }));
  };

  const rowBg = isEdit ? 'bg-blue-100/30' : 'bg-surface-card';

  return (
    <>
      {showLabels && (
        <tr className={cn('border-b border-border-subtle', rowBg)}>
          <td className="w-8 py-2 px-3" />
          <td className="py-2 px-3">
            <FeeRowLabel required>Service name</FeeRowLabel>
          </td>
          <td className="w-20 py-2 px-3 text-right">
            <FeeRowLabel>WTAX, %</FeeRowLabel>
          </td>
          {locations.map((loc) => (
            <PerLocationLabelCells key={`lbl-${loc.id}`} name={loc.name ?? 'Location'} />
          ))}
          <td className="py-2 px-3" />
        </tr>
      )}

      <tr className={cn('border-b border-border-subtle', rowBg)}>
        <td className="w-8 py-3 px-3" />

        <td className="py-3 px-3 align-top">
          <ServiceTypeahead
            value={draft.serviceQuery}
            onTypingChange={(v) => setDraft((p) => ({ ...p, serviceQuery: v }))}
            onSelect={onPickService}
            onClear={clearService}
          />
        </td>

        <td className="w-20 py-3 px-3 align-top">
          <TextField
            type="text"
            size="compact"
            inputMode="decimal"
            placeholder="0.00"
            value={draft.wTax}
            onChange={(e) => setDraft((p) => ({ ...p, wTax: e.target.value }))}
            containerClassName="w-full"
            inputClassName="text-right tabular-nums"
            hideSpinButtons
            aria-label="WTAX percent"
          />
        </td>

        {locations.map((loc, idx) => {
          if (!loc.id) return null;
          const isLastLocation = idx === locations.length - 1;
          const row = draft.fees[loc.id];
          const svcFee = row?.serviceFee;
          const canReset = svcFee != null;
          const share = draft.shares[loc.id];
          return (
            <FeeAndShareCells
              key={loc.id}
              locationName={loc.name ?? 'Location'}
              feeValue={row?.fee ?? ''}
              canReset={canReset}
              share={share}
              isLastLocation={isLastLocation}
              isShareEnabled={hasAnyFee}
              isEdit={isEdit}
              shareCount={shareCount}
              onFeeChange={(v) =>
                setDraft((p) => ({
                  ...p,
                  fees: {
                    ...p.fees,
                    [loc.id!]: { ...(p.fees[loc.id!] ?? { fee: '' }), fee: v },
                  },
                }))
              }
              onResetFee={() =>
                setDraft((p) => ({
                  ...p,
                  fees: {
                    ...p.fees,
                    [loc.id!]: {
                      ...(p.fees[loc.id!] ?? { fee: '' }),
                      fee: svcFee != null ? String(svcFee) : '',
                    },
                  },
                }))
              }
              onOpenShare={() => setShareOpen(true)}
            />
          );
        })}

        <td className="py-3 px-2 align-top">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSave || submitting}
              title="Save"
              aria-label="Save"
              className="px-2 py-1 rounded text-sm font-bold text-status-success hover:bg-status-success-bg disabled:text-text-hint disabled:cursor-not-allowed"
            >
              ✓
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              title={isEdit ? 'Cancel edit' : 'Clear'}
              aria-label={isEdit ? 'Cancel edit' : 'Clear'}
              className="px-2 py-1 rounded text-sm font-bold text-status-error hover:bg-status-error-bg"
            >
              ✕
            </button>
          </div>
        </td>
      </tr>

      <DoctorShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        locations={locations}
        providerFees={draft.fees}
        initialShares={draft.shares}
        onApply={(shares) => {
          setDraft((p) => ({ ...p, shares }));
          setShareOpen(false);
        }}
      />
    </>
  );
}

// ─── Per-location label cells (above the input row) ──────────────────────

function PerLocationLabelCells({ name }: { name: string }) {
  return (
    <>
      <td className="w-24 py-2 px-3">
        <FeeRowLabel>{name}, AED</FeeRowLabel>
      </td>
      <td className="w-40 py-2 px-3" />
    </>
  );
}

// ─── Per-location fee + share cells (in the input row) ───────────────────

interface FeeAndShareCellsProps {
  locationName: string;
  feeValue: string;
  canReset: boolean;
  share: ShareInput | undefined;
  isLastLocation: boolean;
  isShareEnabled: boolean;
  isEdit: boolean;
  shareCount: number;
  onFeeChange: (v: string) => void;
  onResetFee: () => void;
  onOpenShare: () => void;
}

function FeeAndShareCells({
  locationName,
  feeValue,
  canReset,
  share,
  isLastLocation,
  isShareEnabled,
  isEdit,
  shareCount,
  onFeeChange,
  onResetFee,
  onOpenShare,
}: FeeAndShareCellsProps) {
  const preview = formatSharePreview(share);
  return (
    <>
      <td className="w-24 py-3 px-3 align-top">
        <div className="relative">
          <TextField
            type="text"
            size="compact"
            inputMode="decimal"
            placeholder="0.00"
            value={feeValue}
            onChange={(e) => onFeeChange(e.target.value)}
            containerClassName="w-full"
            inputClassName="pr-8 text-right tabular-nums"
            hideSpinButtons
            aria-label={`${locationName} fee`}
          />
          <button
            type="button"
            title="Reset to service default"
            aria-label={`Reset ${locationName} fee to service default`}
            disabled={!canReset}
            onClick={onResetFee}
            className="absolute right-1 top-1/2 -translate-y-1/2 px-1 rounded text-xs text-text-hint hover:text-brand-primary disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↺
          </button>
        </div>
        {preview && (
          <span className="block mt-1 text-xs text-text-muted tabular-nums whitespace-nowrap">
            {preview}
          </span>
        )}
      </td>
      <td className="w-40 py-3 px-3 align-top">
        {isLastLocation && (
          <button
            type="button"
            disabled={!isShareEnabled}
            onClick={onOpenShare}
            className={cn(
              'h-10 w-full px-3 rounded-md text-sm font-semibold whitespace-nowrap transition-colors',
              isShareEnabled
                ? 'bg-brand-primary text-white hover:bg-interactive-hover'
                : 'bg-grey-100 text-text-hint cursor-not-allowed',
            )}
          >
            {isEdit
              ? shareCount > 0
                ? `Edit Doctor Share (${shareCount})`
                : 'Edit Doctor Share'
              : shareCount > 0
                ? `Doctor Share (${shareCount})`
                : 'Add Doctor Share'}
          </button>
        )}
      </td>
    </>
  );
}

/* NOT the design system's FieldLabel. This one is `font-semibold` + `tracking-wider`
 * where the graduated recipe is `font-bold` + `tracking-wide`, so adopting the DS
 * component here would silently restyle it. Renamed on 2026-09-20 only to clear the
 * import collision; the duplication is deliberate and VISIBLE.
 *
 * RULING OWED: is this a second, legitimate label style, or drift from the first? If it
 * is drift these call sites join `FieldLabel`; if it is real the design system owes a
 * variant. Either way it is a design decision, not a migration one. */
function FeeRowLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className="text-xs font-medium text-text-muted whitespace-nowrap">
      {children}
      {required && <span className="text-status-error ml-1">*</span>}
    </span>
  );
}
